import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, AlertTriangle, Users, MapPin, Shield, ArrowRight, Anchor } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const BellinghamSlipFallPage = () => {
  const immediateSteps = [
    {
      title: "Seek medical attention",
      description: "Get checked even for seemingly minor injuries—documentation starts your claim."
    },
    {
      title: "Document the hazard",
      description: "Photograph wet Fairhaven sidewalks, marina ramps, mossy paths, or any dangerous conditions."
    },
    {
      title: "Report to property owner",
      description: "Notify the owner/manager promptly and request a written incident report."
    },
    {
      title: "Collect witness info",
      description: "Get names and contacts from anyone who saw what happened."
    },
    {
      title: "Avoid insurance statements",
      description: "Don't give recorded statements without legal guidance first."
    },
    {
      title: "Call me quickly",
      description: "The 3-year statute runs from injury date, and evidence fades fast in Bellingham's wet climate."
    }
  ];

  const commonCauses = [
    "Rain-tracked water in entries/stores",
    "Uneven outdoor paths or trails",
    "Slippery conditions near waterfront/maritime spots",
    "Poorly maintained stairs in rentals or businesses",
    "Winter ice on steps/sidewalks",
    "Mossy or algae-covered walkways"
  ];

  const compensationTypes = [
    "Medical bills (emergency, surgery, rehab)",
    "Lost wages and future earning capacity",
    "Pain and suffering",
    "Long-term care costs",
    "Diminished quality of life"
  ];

  const faqs = [
    {
      question: "Is a fall on public property claimable?",
      answer: "Often yes—you may have a claim against the city or county if they were negligent in maintaining the property. Special rules apply to government claims."
    },
    {
      question: "How do I prove the hazard existed long enough?",
      answer: "Photos, witness statements, and maintenance records help establish that the property owner knew or should have known about the danger."
    }
  ];

  const faqSchemaData = [
    {
      question: "Can I sue for a slip and fall on public property in Bellingham?",
      answer: "Often yes. You may have a claim against the city or county of Bellingham if they were negligent in maintaining public sidewalks, trails, or buildings. Special notice requirements apply to government claims."
    },
    {
      question: "How do I prove a slip and fall hazard existed long enough in Bellingham?",
      answer: "Evidence includes photos of the hazard, witness statements, maintenance records, weather reports, and any prior complaints about the condition. Amy helps gather this evidence to prove the property owner knew or should have known about the danger."
    },
    {
      question: "Does Amy handle maritime-related slip and falls in Bellingham?",
      answer: "Yes. Amy has specialized experience in maritime-related slips on docks, boat ramps, and marina areas, overlapping with her Jones Act practice. She understands the unique hazards of Bellingham's waterfront."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Bellingham Slip and Fall Lawyer | Whatcom County Injury Attorney | Amy C. Brown"
        description="Injured in a slip and fall in Bellingham or Whatcom County? Amy C. Brown offers transparent, contingency-only representation. Maritime slip expertise. Call (425) 675-1224."
        faqData={faqSchemaData}
        localArea={{ city: "Bellingham", county: "Whatcom", state: "Washington" }}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-secondary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4 text-teal-400" />
              <span className="text-white/90 text-sm font-medium">Serving Bellingham & Whatcom County</span>
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Bellingham Slip and Fall Lawyer – Protecting Injured Visitors in Whatcom County
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-6">
              In Bellingham, slip and falls often occur on wet sidewalks/trails (rainy climate), restaurant floors, tourist spots, grocery stores, apartment stairs, or slippery boat docks/maritime areas.
            </p>

            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              Outdoor hazards like mossy paths or icy steps in winter add risk. The Fairhaven district, downtown sidewalks, and waterfront areas see frequent premises injuries.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
              <p className="text-white leading-relaxed">
                As your Bellingham-area attorney (serving WA & AK), I handle premises cases with <strong>transparency and fight—contingency fees only</strong>. You pay nothing unless we win.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all border border-white/20"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Immediate Steps Section */}
      <section className="py-20 bg-white" data-testid="steps-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              What to Do Right After a Bellingham Fall
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Quick action protects both your recovery and your legal rights.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {immediateSteps.map((step, index) => (
                <div key={index} className="flex gap-4 bg-slate-50 rounded-xl p-6">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary mb-1">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes Section */}
      <section className="py-20 bg-slate-50" data-testid="causes-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Frequent Bellingham Slip and Fall Causes
              </h2>
              <ul className="space-y-4">
                {commonCauses.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-4">
                Your Rights Under WA Law
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Property owners have a duty to maintain safe premises. If negligence caused your fall, you can seek compensation for:
              </p>
              <ul className="space-y-3 mb-6">
                {compensationTypes.map((type, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-slate-700">{type}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <p className="text-sm text-slate-600">
                  <strong>3-year statute of limitations</strong> applies. Washington's comparative negligence means you can recover even if partially at fault.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="py-20 bg-white" data-testid="why-me-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              Why Me for Bellingham Cases?
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Clients value my no-BS approach and specialized experience.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl p-6">
                <Anchor className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Maritime Slip Expertise</h3>
                <p className="text-slate-600">Niche experience in maritime-related slips on docks and ramps, overlapping with my Jones Act work.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Users className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Direct Access</h3>
                <p className="text-slate-600">You work with me—not shuffled between paralegals and associates.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Shield className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Honest Answers</h3>
                <p className="text-slate-600">I'll tell you straight if you have a case—and if you don't. No games.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <CheckCircle className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">WA & AK Coverage</h3>
                <p className="text-slate-600">Serving clients throughout Washington State and Alaska.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-12 text-center">
              Key Questions Answered
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-secondary mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Protect Your Rights Before Time Runs Out
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Text or call <strong className="text-white">(425) 675-1224</strong> for a free consult on your Bellingham slip and fall.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold transition-all"
                data-testid="cta-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/faqs" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              See Full FAQs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Read Blog Posts <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/maritime-injuries" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Maritime Injuries <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BellinghamSlipFallPage;
